/**
 * useLiveCandle — feeds live quote ticks into the focus symbol's candle series
 * so the current (forming) bar updates in real time, and a fresh bar appears at
 * each interval boundary, BETWEEN the 15s poll refetches in MainChart.
 *
 * The 15s poll remains the source of truth: these patches are interpolations it
 * overwrites on its next cycle (which restores real volume and any bars the
 * server has). Intraday intervals only -- daily/weekly/monthly and custom units
 * are left to the poll, because their bucketing is a session-boundary problem,
 * not a simple UTC division. Volume is NEVER fabricated (a quote carries no
 * trade size), so it stays exactly as the server reported it.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '@/store';
import type { Bar } from '@/types';

/**
 * Seconds per intraday interval. Daily+ and custom (d/w/mo) units return null so
 * this hook no-ops for them. Exported for unit testing.
 */
export function intradaySeconds(interval: string): number | null {
  const m = /^(\d+)(s|m|h)$/.exec(interval.trim().toLowerCase());
  if (!m) return null;
  const n = parseInt(m[1], 10);
  const unit = m[2];
  const secs = unit === 's' ? n : unit === 'm' ? n * 60 : n * 3600;
  return secs > 0 && secs < 86400 ? secs : null;
}

/**
 * Pure reducer: given the current bar array and a tick, return the next array.
 * Exported so the bucket/roll logic can be tested without React. Returns the
 * SAME reference when nothing should change, so callers can skip a redraw.
 */
export function applyTick(bars: Bar[], secs: number, price: number, tickTime: number): Bar[] {
  if (!Array.isArray(bars) || bars.length === 0) return bars;
  const last = bars[bars.length - 1];
  const bucket = Math.floor(tickTime / secs) * secs;
  if (bucket <= last.time) {
    // Update the current forming bar in place (close always; high/low if broken).
    if (price === last.close && price <= last.high && price >= last.low) return bars;
    const updated: Bar = {
      ...last,
      close: price,
      high:  Math.max(last.high, price),
      low:   Math.min(last.low, price),
    };
    return [...bars.slice(0, -1), updated];
  }
  // Boundary crossed -> roll a fresh bar. open == first tick of the new period;
  // volume starts at 0 and the next poll fills the real figure.
  const fresh: Bar = { time: bucket, open: price, high: price, low: price, close: price, volume: 0 };
  return [...bars, fresh];
}

export function useLiveCandle(focus: string, period: string, interval: string): void {
  const qc = useQueryClient();
  const quote = useStore(s => s.quotes[focus]);
  const lastPatch = useRef(0);

  useEffect(() => {
    if (!quote) return;
    const price = Number(quote.last);
    if (!Number.isFinite(price) || price <= 0) return;
    const secs = intradaySeconds(interval);
    if (secs == null) return;                    // daily+ / custom -> the poll owns it

    // Throttle live redraws: rebuilding the Plotly figure on every tick of a
    // fast ticker is wasteful, and sub-second candle precision is imperceptible.
    const now = Date.now();
    if (now - lastPatch.current < 350) return;
    lastPatch.current = now;

    const tickTime = Number.isFinite(quote.timestamp) ? quote.timestamp : Math.floor(now / 1000);
    qc.setQueryData<Bar[]>(['bars', focus, period, interval], (old) =>
      Array.isArray(old) ? applyTick(old, secs, price, tickTime) : old,
    );
  }, [quote, focus, period, interval, qc]);
}
